import { verificationAdminService } from "@/api/admin/verification-admin.service";
import type { TAuditLogRecord } from "@/api/admin/verification-admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TDateRangeValue,
  type TFilterDef,
} from "@/components/data-grid";
import { Lock } from "lucide-react";

const PAGE_SIZE = 20;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function Detail({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`mt-0.5 break-all text-sm ${mono ? "font-mono text-xs" : ""}`}>{value ?? "-"}</p>
    </div>
  );
}

export function VerificationAuditLogsPage() {
  const fetcher = async (query: TDataGridQuery) => {
    const dateRange = (query.filters.created as TDateRangeValue | undefined) ?? {};
    const channel = asString(query.filters.channel);
    const severity = asString(query.filters.severity);
    const response = await verificationAdminService.listAuditLogs(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
        ...(asString(query.filters.user_id) ? { user_id: asString(query.filters.user_id) } : {}),
        ...(channel === "email" || channel === "phone" ? { channel } : {}),
        ...(severity === "INFO" || severity === "WARNING" || severity === "HIGH" || severity === "CRITICAL"
          ? { severity }
          : {}),
        ...(asString(query.filters.event) ? { event: asString(query.filters.event) } : {}),
        ...(dateRange.from ? { date_from: dateRange.from } : {}),
        ...(dateRange.to ? { date_to: dateRange.to } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  // Backend-whitelisted sort columns for verification/audit-logs: created_at only.
  const columns: TColumnDef<TAuditLogRecord>[] = [
    {
      id: "event",
      header: "Event",
      accessor: (r) => r.event ?? "",
      cell: (r) => (
        <span className="inline-flex items-center gap-1 text-sm font-medium">
          <span className="truncate">{r.event}</span>
          {r.legal_hold && (
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <Lock className="h-2.5 w-2.5" />
              Hold
            </span>
          )}
        </span>
      ),
    },
    {
      id: "channel",
      header: "Channel",
      type: "badge",
      accessor: (r) => r.channel ?? "",
      meta: { badgeVariant: () => "outline" },
    },
    {
      id: "severity",
      header: "Severity",
      type: "badge",
      accessor: (r) => r.severity,
      meta: {
        badgeVariant: (v) =>
          v === "CRITICAL" ? "destructive" : v === "HIGH" || v === "WARNING" ? "outline" : "secondary",
      },
    },
    {
      id: "ip_address",
      header: "IP",
      accessor: (r) => r.ip_address ?? "",
      cell: (r) => (
        <span className="block max-w-xs truncate font-mono text-xs text-muted-foreground">
          {r.ip_address ?? "-"}
        </span>
      ),
    },
    { id: "user_id", header: "User ID", type: "uuid", defaultHidden: true, accessor: (r) => r.user_id },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    { id: "event", type: "text", label: "Event", placeholder: "Event (e.g. OTP_FAILED)" },
    {
      id: "channel",
      type: "select",
      label: "Channel",
      options: [
        { label: "Email", value: "email" },
        { label: "Phone", value: "phone" },
      ],
    },
    {
      id: "severity",
      type: "select",
      label: "Severity",
      options: [
        { label: "Info", value: "INFO" },
        { label: "Warning", value: "WARNING" },
        { label: "High", value: "HIGH" },
        { label: "Critical", value: "CRITICAL" },
      ],
    },
    { id: "created", type: "dateRange", label: "Date" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Verification Audit Logs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Security events across email and WhatsApp OTP verification
          </p>
        </div>

        <DataGrid<TAuditLogRecord>
          getRowId={(row) => row.id}
          persistKey="admin.verification.audit"
          ariaLabel="Verification audit logs"
          data={{ mode: "server", fetcher }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search event",
          }}
          details={{
            variant: "drawer",
            title: (row) => row.event,
            render: (row) => (
              <div className="space-y-4 p-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Detail label="Record ID" value={row.id} mono />
                  <Detail label="User ID" value={row.user_id} mono />
                  <Detail label="Verification Request ID" value={row.verification_request_id} mono />
                  <Detail label="Channel" value={row.channel} />
                  <Detail label="Severity" value={row.severity} />
                  <Detail label="IP Address" value={row.ip_address} mono />
                  <Detail label="Created At" value={fmtDate(row.created_at)} />
                  <Detail label="User Agent" value={row.user_agent} />
                </div>
                {row.event_metadata && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Event Metadata</p>
                    <pre className="max-h-60 overflow-auto rounded bg-muted p-2 text-xs">
                      {JSON.stringify(row.event_metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ),
          }}
          emptyState={{ title: "No verification audit logs found." }}
        />
      </div>
    </div>
  );
}
